import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "../../../context/AuthContext";
import { usePlans } from "../../../context/PlanContext";
import FulfillmentDialog from "../FulfillmentDialog";
import api from "../../../services/api";

interface ServiceItem {
  id: string;
  taskId: string;
  studentId: string;
  studentName: string;
  taskTitle: string;
  priority: string;
  status: "OPEN" | "ASSIGNED" | "COMPLETED" | "RESOLVED";
  submissionStatus: string | null;
  createdAt: string;
  type: string;
  taskType: "LINK" | "IMAGE" | "TEXT";
}

export default function ExpertPool() {
  const navigate = useNavigate();
  const { data: session }: any = useSession();
  const { getPlanByName } = usePlans();
  const [items, setItems] = useState<ServiceItem[]>([]);
  const [activeTab, setActiveTab] = useState<"LIVE" | "HISTORY">("LIVE");
  const [fulfillmentItem, setFulfillmentItem] = useState<ServiceItem | null>(null);

  const fetchPool = async () => {
    try {
      const res = await api.get(`/service-pool?t=${Date.now()}`);
      const data = Array.isArray(res.data) ? res.data : [];
      
      // Sort: Priority (VIP > PREMIUM > GROWTH) then Newest
      const sorted = [...data].sort((a, b) => {
        const pA = getPlanByName(a.priority)?.tier_level ?? 0;
        const pB = getPlanByName(b.priority)?.tier_level ?? 0;
        if (pA !== pB) return pB - pA;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(); 
      });
      setItems(sorted);
    } catch (err) {
      console.error("Failed to fetch expert pool:", err);
    }
  };

  useEffect(() => {
    fetchPool();
    const interval = setInterval(fetchPool, 10000);
    return () => clearInterval(interval);
  }, []);

  const filteredItems = items.filter(item => {
    if (item.type !== "EXPERT_JOB") return false;
    if (activeTab === "LIVE") {
      return item.status === "OPEN" || item.status === "ASSIGNED";
    } else {
      return item.status === "COMPLETED";
    }
  });

  const handleClaim = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try {
      await api.patch(`/service-pool/${id}`, { 
        status: "ASSIGNED", 
        type: "EXPERT_JOB", 
        staffId: session?.user?.id 
      });
      fetchPool();
    } catch (err) {
      console.error("Claim failed:", err);
    }
  };

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case "OPEN": return { label: "Open Draft", color: "bg-amber-500" };
      case "ASSIGNED": return { label: "Fulfilling", color: "bg-blue-500" };
      case "COMPLETED": return { label: "Executed", color: "bg-emerald-500" };
      default: return { label: status, color: "bg-slate-400" };
    }
  };

  return (
    <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700 pb-20">
      <FulfillmentDialog 
        isOpen={!!fulfillmentItem} 
        onClose={() => setFulfillmentItem(null)}
        item={fulfillmentItem}
        onSuccess={fetchPool}
      />

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-6 border-b border-outline-variant/10">
        <div>
          <h2 className="text-3xl font-black text-on-surface tracking-tighter">Execution Pool</h2>
          <p className="text-on-surface-variant font-medium text-sm italic">High-velocity fulfillment center for student module deliverables.</p>
        </div>

        <div className="flex items-center bg-surface-container-high p-1 rounded-2xl border border-outline-variant/10 shadow-sm">
          <button 
            onClick={() => setActiveTab("LIVE")}
            className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all cursor-pointer ${
              activeTab === "LIVE" ? "bg-on-surface text-surface shadow-lg font-bold" : "text-on-surface-variant hover:text-on-surface"
            }`}
          >
            Live Queue ({items.filter(i => i.type === "EXPERT_JOB" && (i.status === "OPEN" || i.status === "ASSIGNED")).length})
          </button>
          <button 
            onClick={() => setActiveTab("HISTORY")}
            className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all cursor-pointer ${
              activeTab === "HISTORY" ? "bg-on-surface text-surface shadow-lg font-bold" : "text-on-surface-variant hover:text-on-surface"
            }`}
          >
            History
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredItems.map((item) => {
          const sDisplay = getStatusDisplay(item.status);
          return (
            <div 
              key={item.id}
              onClick={() => navigate(`/tasks/${item.taskId}?userId=${item.studentId}`)}
              className={`bg-surface-container-low border rounded-[2.5rem] p-7 transition-all hover:shadow-2xl hover:translate-y-[-6px] flex flex-col justify-between gap-6 border-t-8 h-full group cursor-pointer ${
                item.status === "OPEN" ? "border-amber-500 bg-amber-500/5" :
                item.status === "ASSIGNED" ? "border-blue-500 bg-blue-500/5" : 
                "border-emerald-500 bg-emerald-500/5"
              } ${activeTab === "HISTORY" ? "opacity-60 grayscale-[0.3]" : ""}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-black text-on-surface-variant/40 uppercase tracking-[0.2em]">#{item.id.slice(-6).toUpperCase()}</span>
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-xs text-on-surface-variant/40">schedule</span>
                  <span className="text-[10px] font-bold text-on-surface-variant/60">
                     {new Date(item.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>

              <div className="space-y-3 flex-1">
                <div className="space-y-0.5">
                  <p className="text-[10px] font-black text-on-surface-variant uppercase tracking-[0.15em] opacity-40">Client Student</p>
                  <h4 className="text-xl font-black text-on-surface tracking-tighter leading-tight group-hover:text-primary transition-colors">
                    {item.studentName}
                  </h4>
                </div>
                <div className="h-px w-8 bg-outline-variant/20" />
                <div className="space-y-0.5">
                  <p className="text-[10px] font-black text-on-surface-variant uppercase tracking-[0.15em] opacity-40">Production Task</p>
                  <p className="text-sm font-bold text-on-surface-variant tracking-tight line-clamp-2">{item.taskTitle}</p>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-outline-variant/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full animate-pulse ${sDisplay.color}`} />
                    <span className="text-[10px] font-black uppercase tracking-widest text-on-surface-variant/70">{sDisplay.label}</span>
                  </div>
                  <span 
                    className="text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-tighter"
                    style={{
                      backgroundColor: getPlanByName(item.priority)?.icon_color ? `${getPlanByName(item.priority)?.icon_color}1A` : (
                        item.priority === "VIP" ? "rgba(6,182,212,0.1)" : 
                        item.priority === "PREMIUM" ? "rgba(245,158,11,0.1)" : 
                        "rgba(100,116,139,0.1)"
                      ),
                      color: getPlanByName(item.priority)?.icon_color || (
                        item.priority === "VIP" ? "#22d3ee" : 
                        item.priority === "PREMIUM" ? "#fbbf24" : 
                        "#94a3b8"
                      )
                    }}
                  >
                    {item.priority}
                  </span>
                </div>

                {item.status === "OPEN" ? (
                  <button 
                    onClick={(e) => handleClaim(e, item.id)}
                    className="w-full py-4 bg-amber-600 text-white rounded-[1.5rem] font-black text-[11px] uppercase tracking-widest hover:translate-y-[-2px] hover:shadow-xl transition-all active:scale-[0.98] shadow-lg shadow-amber-600/20 cursor-pointer"
                  >
                    Claim Deliverable
                  </button>
                ) : item.status === "ASSIGNED" ? (
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setFulfillmentItem(item);
                    }}
                    className="w-full py-4 bg-blue-600 text-white rounded-[1.5rem] font-black text-[11px] uppercase tracking-widest hover:translate-y-[-2px] hover:shadow-xl transition-all shadow-lg shadow-blue-600/20 active:scale-[0.98] cursor-pointer"
                  >
                    Submit Execution
                  </button>
                ) : (
                  <div className="w-full py-4 bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 rounded-[1.5rem] font-black text-[11px] uppercase tracking-widest flex items-center justify-center gap-2">
                    <span className="material-symbols-outlined text-sm">check_circle</span>
                    Asset Delivered
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {filteredItems.length === 0 && (
          <div className="col-span-full py-24 text-center space-y-4 bg-surface-container-lowest/50 rounded-[3rem] border-2 border-dashed border-outline-variant/10">
            <span className="material-symbols-outlined text-7xl text-on-surface-variant/20">auto_awesome</span>
            <p className="text-on-surface-variant font-medium italic opacity-60">Execution pool is currently clear. Ready for new assets.</p>
          </div>
        )}
      </div>
    </div>
  );
}
